/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';

import { normalizeContentContributorsData } from '../normalizers/normalizeContentContributorsData';

import type {
	useContentContributorsDetailsPanelContentContributorsQuery as useContentContributorsDetailsPanelContentContributorsQuery$data,
	useContentContributorsDetailsPanelContentContributorsQueryVariables as useContentContributorsDetailsPanelContentContributorsQuery$variables,
} from './__types__/useContentContributorsDetailsPanelContentContributorsQuery';
import { useContentOwner } from './useContentOwner';
import { getFormattedNodes } from './contributorUtils';

const useContentContributorsDetailsPanelContentContributorsQuery = gql`
	query useContentContributorsDetailsPanelContentContributorsQuery(
		$contentId: ID!
		$contributorLimit: Int!
	) {
		contentContributors(id: $contentId, limit: $contributorLimit) {
			nodes {
				type
				displayName
				... on KnownUser {
					accountId
				}
				... on User {
					accountId
				}
				profilePicture {
					path
				}
			}
		}
	}
`;
export const MAX_32BIT_INT = 2147483647;

export const useContentContributors = () => {
	const [contentId] = usePageContentId();
	const {
		data: contentOwnerData,
		refetch: refetchContentOwner,
		error: contentOwnerError,
		loading: isContentOwnerLoading,
	} = useContentOwner();

	const { accountId: ownerAccountId } = contentOwnerData;

	const {
		data: contentContributorsData,
		loading: isContentContributorsLoading,
		error: contentContributorsError,
		refetch: refetchContentContributors,
	} = useQuery<
		useContentContributorsDetailsPanelContentContributorsQuery$data,
		useContentContributorsDetailsPanelContentContributorsQuery$variables
	>(useContentContributorsDetailsPanelContentContributorsQuery, {
		variables: {
			contentId: contentId ?? '',
			contributorLimit: MAX_32BIT_INT,
		},
		skip: !contentId,
	});
	const formattedNodes = useMemo(() => {
		const { nodes } = normalizeContentContributorsData(contentContributorsData);

		return getFormattedNodes({ nodes, ownerAccountId });
	}, [contentContributorsData, ownerAccountId]);

	return {
		data: {
			nodes: formattedNodes,
			count: formattedNodes.length,
		},
		loading: isContentOwnerLoading || isContentContributorsLoading,
		error: contentOwnerError || contentContributorsError,
		refetch: contentOwnerError ? refetchContentOwner : refetchContentContributors,
	};
};
